import { type JSX, useEffect, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

interface RecordButtonProps {
  children: ({
    disabled,
    onClick,
  }: {
    disabled: boolean;
    onClick: () => void;
  }) => JSX.Element;
  onRecord: () => void;
}

type PermissionStatus = globalThis.PermissionState;

export default function RecordAudioButton({
  children,
  onRecord,
}: RecordButtonProps) {
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>();
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  useEffect(() => {
    (async () => {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        setPermissionStatus(permission.state);

        permission.onchange = () => {
          setPermissionStatus(permission.state);
        };
      }
    })();
  }, []);

  async function requestPermission() {
    if (permissionStatus !== "granted") {
      setIsRequestingPermission(true);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());

        setPermissionStatus("granted");
        onRecord();
      } catch (_error) {
        setPermissionStatus("denied");
      } finally {
        setIsRequestingPermission(false);
      }
    }
  }

  const buttonProps = {
    disabled: permissionStatus === "denied" || isRequestingPermission,
    onClick: () => {
      if (permissionStatus === "granted") {
        onRecord();
      } else {
        requestPermission();
      }
    },
  };

  if (buttonProps.disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{children(buttonProps)}</TooltipTrigger>
        <TooltipContent>
          Please enable microphone access in your browser settings to continue.
        </TooltipContent>
      </Tooltip>
    );
  }

  return children(buttonProps);
}
